import type {
  BackendRequestName,
  GetActionArgumentsType,
  GetResponseDataType,
} from "@/base/send-request"

import * as ReduxJsToolkit from "@reduxjs/toolkit"

import type {
  MissingPermissions,
  RequestBlockedInDemo,
} from "@/base/check-permissions"
import type { RootState } from "./root.reducer"
import type { RootDispatch } from "./store"

type RejectValue = MissingPermissions | RequestBlockedInDemo

/**
 * RTK's default ``miniSerializeError`` only copies string-typed
 * ``name/message/stack/code``, so numeric fields on our custom error
 * classes (notably ``BadRequestError.deviceReturnCode``) are dropped
 * when ``unwrap()`` rethrows. Keep it so the toast pipeline in
 * ``useErrorHandler`` can run ``translateDeviceReturnCode`` instead of
 * falling back to "Something went wrong".
 */
export interface SerializedRootError extends ReduxJsToolkit.SerializedError {
  deviceReturnCode?: number
}

export interface RootThunkConfig {
  dispatch: RootDispatch
  rejectValue?: RejectValue
  serializedErrorType: SerializedRootError
  state: RootState
}

function serializeRootError(value: unknown): SerializedRootError {
  const base = ReduxJsToolkit.miniSerializeError(value) as SerializedRootError
  if (typeof value === "object" && value !== null) {
    const deviceReturnCode = (value as { deviceReturnCode?: unknown })
      .deviceReturnCode
    if (typeof deviceReturnCode === "number") {
      base.deviceReturnCode = deviceReturnCode
    }
  }
  return base
}

export type GetAsyncThunkType<
  N extends BackendRequestName,
  R extends GetResponseDataType<N> = GetResponseDataType<N>,
  A = GetActionArgumentsType<N>,
> = ReduxJsToolkit.AsyncThunk<R, A, RootThunkConfig> & {
  typePrefix: N
}

const typedCreateAsyncThunk =
  ReduxJsToolkit.createAsyncThunk.withTypes<RootThunkConfig>()

export const createAsyncThunk: typeof typedCreateAsyncThunk = ((
  typePrefix: Parameters<typeof typedCreateAsyncThunk>[0],
  payloadCreator: Parameters<typeof typedCreateAsyncThunk>[1],
  options?: Parameters<typeof typedCreateAsyncThunk>[2],
) =>
  typedCreateAsyncThunk(typePrefix, payloadCreator, {
    serializeError: serializeRootError,
    ...options,
  })) as typeof typedCreateAsyncThunk
